<template>
  <div class="schedule-gantt">
    <el-form :inline="true" :model="q" size="small">
      <el-form-item label="车间">
        <el-select v-model="q.workShopId" clearable filterable placeholder="全部" style="width:160px" @change="load">
          <el-option v-for="w in workShops" :key="w.workShopId" :label="w.workShopName" :value="w.workShopId" />
        </el-select>
      </el-form-item>
      <el-form-item label="期间起"><el-date-picker v-model="q.rangeStart" type="date" value-format="yyyy-MM-dd" @change="load" /></el-form-item>
      <el-form-item label="期间止"><el-date-picker v-model="q.rangeEnd" type="date" value-format="yyyy-MM-dd" @change="load" /></el-form-item>
      <el-form-item><el-button type="primary" @click="load">刷新</el-button></el-form-item>
      <el-form-item>
        <el-button v-if="isAuth('production:productionSchedule:auto')" type="warning" :loading="autoLoading" @click="runAuto">自动排程</el-button>
      </el-form-item>
      <el-form-item>
        <el-button v-if="isAuth('production:productionSchedule:optimize')" type="success" :loading="optLoading" @click="runOptimize">优化排程</el-button>
      </el-form-item>
    </el-form>
    <div class="gantt-tip">蓝色=自动排程，橙色=手工排程，灰色=停机<span v-if="isAuth('production:productionSchedule:move')">；可拖拽任务改时间/机台</span></div>
    <div v-loading="loading" class="gantt-wrap" ref="ganttWrap">
      <div class="gantt-header">
        <div class="gantt-label-col">机台</div>
        <div class="gantt-timeline-col">
          <div class="gantt-days">
            <div v-for="d in dayHeaders" :key="d.key" class="gantt-day" :style="{ width: dayWidth + 'px' }">{{ d.label }}</div>
          </div>
        </div>
      </div>
      <div v-for="(row, rowIdx) in ganttRows" :key="row.resource.workCenterId" class="gantt-row" :data-row-idx="rowIdx">
        <div class="gantt-label-col" :title="row.resource.workShopName">
          <div class="gantt-label-name">{{ row.resource.workCenterCode }} {{ row.resource.workCenterName }}</div>
          <div class="gantt-label-capacity">并行数 {{ row.resource.parallelQty || 1 }}</div>
        </div>
        <div class="gantt-timeline-col">
          <div class="gantt-track" :style="{ width: timelineWidth + 'px', height: (row.lanes * 32 + 8) + 'px' }">
            <div v-for="d in row.downtimes" :key="d.downtimeId" class="gantt-downtime"
              :style="downtimeStyle(d)" :title="downtimeTitle(d)" />
            <div
              v-for="t in row.tasks"
              :key="t.scheduleTaskId"
              class="gantt-bar"
              :class="{ dragging: dragging && dragging.task.scheduleTaskId === t.scheduleTaskId }"
              :style="barStyle(t)"
              :title="barTitle(t)"
              @mousedown.prevent="onBarDown($event, t, row.resource, rowIdx)"
            >{{ t.productionOrderCode }}</div>
          </div>
        </div>
      </div>
      <div v-if="!loading && !resources.length" class="gantt-empty">暂无启用机台，请先维护「工作中心」。</div>
      <div v-else-if="!loading && !tasks.length" class="gantt-empty">当前期间暂无排程任务。</div>
    </div>
  </div>
</template>

<script>
import {
  getScheduleGantt,
  autoProductionSchedule,
  optimizeProductionSchedule,
  moveScheduleTask
} from '../../api/production/productionOrder'
import request from '../../api/request'
import { fetchPerms } from '../../api/auth'

function today() {
  const d = new Date()
  return formatDay(d)
}

function formatDay(d) {
  const p = n => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
}

function nextDay(day) {
  const date = new Date(day + 'T00:00:00')
  date.setDate(date.getDate() + 1)
  return formatDay(date)
}

function formatDt(date) {
  const d = date instanceof Date ? date : new Date(date)
  const p = n => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds())
}

export default {
  name: 'ProductionScheduleGantt',
  data() {
    return {
      q: { workShopId: '', rangeStart: today(), rangeEnd: '' },
      workShops: [], resources: [], tasks: [], downtimes: [], rangeStart: null, rangeEnd: null,
      loading: false, autoLoading: false, optLoading: false, dayWidth: 120, dragging: null, permCodes: []
    }
  },
  computed: {
    dayHeaders() {
      if (!this.rangeStart || !this.rangeEnd) return []
      const list = []
      const s = new Date(this.rangeStart)
      const e = new Date(this.rangeEnd)
      s.setHours(0, 0, 0, 0)
      e.setHours(0, 0, 0, 0)
      for (let d = new Date(s); d < e; d.setDate(d.getDate() + 1)) {
        const key = formatDay(d)
        list.push({ key, label: key.slice(5) })
      }
      return list
    },
    timelineWidth() { return Math.max(this.dayHeaders.length * this.dayWidth, 600) },
    totalMs() {
      if (!this.rangeStart || !this.rangeEnd) return 1
      return Math.max(1, new Date(this.rangeEnd).getTime() - new Date(this.rangeStart).getTime())
    },
    ganttRows() {
      return this.resources.map(resource => {
        const laneEnds = []
        const tasks = this.tasks.filter(t => t.workCenterId === resource.workCenterId)
          .slice().sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart))
          .map(task => {
            const start = new Date(task.scheduledStart).getTime()
            const end = new Date(task.scheduledEnd).getTime()
            let lane = laneEnds.findIndex(freeAt => freeAt <= start)
            if (lane < 0) lane = laneEnds.length
            laneEnds[lane] = end
            return { ...task, visualLane: lane }
          })
        return { resource, tasks, downtimes: this.downtimes.filter(d => d.workCenterId === resource.workCenterId), lanes: Math.max(1, laneEnds.length) }
      })
    }
  },
  created() { this.loadPerms(); this.loadWorkShops(); this.load() },
  beforeDestroy() { this.unbindDrag() },
  methods: {
    isAuth(perm) {
      const user = this.$store.state.user || {}
      const codes = Array.isArray(user.roleCodes) ? user.roleCodes : []
      if (['TENANT_ADMIN', 'PLATFORM_ADMIN'].indexOf(String(user.roleCode || '')) >= 0 || codes.indexOf('TENANT_ADMIN') >= 0 || codes.indexOf('PLATFORM_ADMIN') >= 0) return true
      return this.permCodes.indexOf(perm) >= 0
    },
    loadPerms() { fetchPerms().then(res => { this.permCodes = res.data || [] }).catch(() => {}) },
    loadWorkShops() {
      request.get('/base/workShop/selectEnabled').then(res => { this.workShops = res.data || [] }).catch(() => {})
    },
    validRange() {
      if (this.q.rangeStart && this.q.rangeEnd && this.q.rangeEnd < this.q.rangeStart) {
        this.$message.warning('期间止不能早于期间起')
        this.resources = []
        this.tasks = []
        this.downtimes = []
        this.rangeStart = null
        this.rangeEnd = null
        return false
      }
      return true
    },
    load() {
      if (!this.validRange()) return
      this.loading = true
      getScheduleGantt(this.schedulePayload()).then(res => {
        const body = res.data || {}
        this.resources = body.resources || []
        this.tasks = body.tasks || []
        this.downtimes = body.downtimes || []
        this.rangeStart = body.rangeStart
        this.rangeEnd = body.rangeEnd
      }).catch(() => {
        this.resources = []
        this.tasks = []
        this.downtimes = []
        this.rangeStart = null
        this.rangeEnd = null
      }).finally(() => { this.loading = false })
    },
    barStyle(t) {
      const start = new Date(t.scheduledStart).getTime()
      const end = new Date(t.scheduledEnd).getTime()
      const base = new Date(this.rangeStart).getTime()
      const left = ((start - base) / this.totalMs) * 100
      const width = Math.max(((end - start) / this.totalMs) * 100, 0.5)
      const style = { left: left + '%', width: width + '%', top: (4 + t.visualLane * 32) + 'px', background: t.sourceType === 'manual' ? '#e6a23c' : '#409eff' }
      if (this.dragging && this.dragging.task.scheduleTaskId === t.scheduleTaskId) style.left = this.dragging.previewLeft + '%'
      return style
    },
    downtimeStyle(d) {
      const start = Math.max(new Date(d.downtimeStart).getTime(), new Date(this.rangeStart).getTime())
      const end = Math.min(new Date(d.downtimeEnd).getTime(), new Date(this.rangeEnd).getTime())
      return { left: ((start - new Date(this.rangeStart).getTime()) / this.totalMs * 100) + '%',
        width: (Math.max(0, end - start) / this.totalMs * 100) + '%' }
    },
    downtimeTitle(d) {
      return `停机${d.reason ? '：' + d.reason : ''}\n${(d.downtimeStart || '').slice(0, 16)} ~ ${(d.downtimeEnd || '').slice(0, 16)}`
    },
    barTitle(t) {
      return `${t.productionOrderCode} ${t.processName || ''}\n${(t.scheduledStart || '').slice(0, 16)} ~ ${(t.scheduledEnd || '').slice(0, 16)}`
    },
    schedulePayload() {
      return {
        workShopId: this.q.workShopId,
        rangeStart: this.q.rangeStart ? this.q.rangeStart + ' 00:00:00' : null,
        rangeEnd: this.q.rangeEnd ? nextDay(this.q.rangeEnd) + ' 00:00:00' : null,
        overwrite: true
      }
    },
    runAuto() {
      if (!this.validRange()) return
      this.$confirm('将按工单工艺节拍与机台负荷重新生成自动排程，是否继续？', '自动排程', { type: 'warning' }).then(() => {
        this.autoLoading = true
        autoProductionSchedule(this.schedulePayload()).then(res => {
          const count = (res.data && res.data.scheduledOrderCount) || 0
          this.$message.success('已排程 ' + count + ' 张工单')
          this.load()
          this.$emit('scheduled')
        }).catch(() => {}).finally(() => { this.autoLoading = false })
      }).catch(() => {})
    },
    runOptimize() {
      if (!this.validRange()) return
      this.$confirm('将重新执行排程以降低机台峰值负荷，是否继续？', '优化排程', { type: 'warning' }).then(() => {
        this.optLoading = true
        optimizeProductionSchedule(this.schedulePayload()).then(res => {
          const count = (res.data && res.data.scheduledOrderCount) || 0
          this.$message.success('优化完成，已排程 ' + count + ' 张工单')
          this.load()
          this.$emit('scheduled')
        }).catch(() => {}).finally(() => { this.optLoading = false })
      }).catch(() => {})
    },
    onBarDown(e, t, res, rowIdx) {
      if (!this.isAuth('production:productionSchedule:move')) return
      const start = new Date(t.scheduledStart).getTime()
      const base = new Date(this.rangeStart).getTime()
      this.dragging = { task: t, centerId: res.workCenterId, rowIdx, startX: e.clientX, origStartMs: start, previewLeft: ((start - base) / this.totalMs) * 100 }
      document.addEventListener('mousemove', this.onBarMove)
      document.addEventListener('mouseup', this.onBarUp)
    },
    onBarMove(e) {
      if (!this.dragging) return
      const deltaX = e.clientX - this.dragging.startX
      const deltaMs = (deltaX / this.timelineWidth) * this.totalMs
      const newStart = this.dragging.origStartMs + deltaMs
      const base = new Date(this.rangeStart).getTime()
      this.dragging.previewLeft = ((newStart - base) / this.totalMs) * 100
      this.dragging.deltaMs = deltaMs
      const rows = this.$refs.ganttWrap.querySelectorAll('.gantt-row')
      for (let i = 0; i < rows.length; i++) {
        const rect = rows[i].getBoundingClientRect()
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          this.dragging.targetRowIdx = i
          break
        }
      }
    },
    onBarUp() {
      if (!this.dragging) return
      const d = this.dragging
      this.unbindDrag()
      const deltaMs = d.deltaMs || 0
      const newStartMs = d.origStartMs + deltaMs
      let newCenterId = d.centerId
      if (d.targetRowIdx != null && this.resources[d.targetRowIdx]) newCenterId = this.resources[d.targetRowIdx].workCenterId
      this.dragging = null
      if (Math.abs(deltaMs) < 60000 && newCenterId === d.centerId) return
      const targetCenter = this.resources.find(item => item.workCenterId === newCenterId)
      if (targetCenter && String(targetCenter.workShopId || '') !== String(d.task.workShopId || '')) {
        this.$message.warning('机台必须属于任务所在车间')
        return
      }
      moveScheduleTask({
        scheduleTaskId: d.task.scheduleTaskId,
        workCenterId: newCenterId,
        scheduledStart: formatDt(newStartMs)
      }).then(() => {
        this.$message.success('已更新排程')
        this.load()
        this.$emit('scheduled')
      }).catch(() => this.load())
    },
    unbindDrag() {
      document.removeEventListener('mousemove', this.onBarMove)
      document.removeEventListener('mouseup', this.onBarUp)
    }
  }
}
</script>

<style scoped>
.gantt-tip { font-size: 12px; color: #909399; margin-bottom: 8px; }
.gantt-wrap { border: 1px solid #ebeef5; overflow: auto; max-height: 520px; }
.gantt-header, .gantt-row { display: flex; border-bottom: 1px solid #ebeef5; }
.gantt-label-col { width: 180px; flex-shrink: 0; padding: 8px; font-size: 12px; background: #f5f7fa; border-right: 1px solid #ebeef5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gantt-row .gantt-label-col { display: flex; flex-direction: column; justify-content: center; }
.gantt-label-name { overflow: hidden; text-overflow: ellipsis; }
.gantt-label-capacity { color: #909399; margin-top: 3px; }
.gantt-timeline-col { flex: 1; overflow: hidden; }
.gantt-days { display: flex; height: 32px; line-height: 32px; font-size: 12px; color: #606266; border-bottom: 1px solid #ebeef5; }
.gantt-day { text-align: center; border-right: 1px solid #f0f0f0; flex-shrink: 0; }
.gantt-track { position: relative; margin: 4px 0; background: repeating-linear-gradient(90deg, #fafafa, #fafafa 119px, #f0f0f0 120px); }
.gantt-downtime { position: absolute; top: 0; bottom: 0; background: repeating-linear-gradient(135deg, #d3d7de, #d3d7de 5px, #e4e7ed 5px, #e4e7ed 10px); opacity: 0.85; }
.gantt-bar { position: absolute; top: 4px; height: 28px; line-height: 28px; font-size: 11px; color: #fff; padding: 0 4px; overflow: hidden; border-radius: 3px; white-space: nowrap; cursor: grab; user-select: none; }
.gantt-bar.dragging { opacity: 0.85; cursor: grabbing; z-index: 2; }
.gantt-empty { padding: 24px; text-align: center; color: #909399; }
</style>
